/**
 * Creates a metallic/jewelry linear gradient for a given hex color
 * This gives colors a shiny, 3D appearance like real jewelry with golden highlights
 */
export const getJewelryGradient = (hexColor: string): string => {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Create a bright golden highlight (warm white with gold tint)
  const highlightR = Math.min(255, r + 90);
  const highlightG = Math.min(255, g + 70);
  const highlightB = Math.min(255, Math.max(b + 30, b * 0.8));

  // Create a secondary shine point (slightly golden)
  const shineR = Math.min(255, r + 50);
  const shineG = Math.min(255, g + 40);
  const shineB = Math.min(255, b + 15);

  // Create darker shadow for depth
  const shadowR = Math.max(0, r - 50);
  const shadowG = Math.max(0, g - 45);
  const shadowB = Math.max(0, b - 35);

  const highlight = `rgb(${highlightR}, ${highlightG}, ${highlightB})`;
  const shine = `rgb(${shineR}, ${shineG}, ${shineB})`;
  const base = `rgb(${r}, ${g}, ${b})`;
  const shadow = `rgb(${shadowR}, ${shadowG}, ${shadowB})`;

  // Create a multi-stop gradient that simulates metallic reflection with golden shine
  return `linear-gradient(145deg, ${highlight} 0%, ${shine} 25%, ${base} 50%, ${shine} 75%, ${shadow} 100%)`;
};

/**
 * Creates a shimmer gradient overlay for jewelry effect
 * Returns both the base gradient and shimmer overlay as a combined background
 */
export const getJewelryShimmerGradient = (hexColor: string): string => {
  const baseGradient = getJewelryGradient(hexColor);
  return baseGradient;
};

/**
 * Creates CSS style object for jewelry gradient with shimmer animation
 */
export const getJewelryGradientStyle = (hexColor: string): React.CSSProperties => {
  return {
    background: getJewelryGradient(hexColor),
  };
};

/**
 * Creates CSS style object for jewelry gradient with animated shimmer overlay
 */
export const getJewelryShimmerStyle = (hexColor: string): React.CSSProperties => {
  // Convert hex to RGB for shimmer highlight calculation
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Shimmer highlight color (bright golden/white)
  const shimmerR = Math.min(255, r + 120);
  const shimmerG = Math.min(255, g + 100);
  const shimmerB = Math.min(255, b + 60);

  return {
    background: `${getJewelryGradient(hexColor)}`,
    position: 'relative' as const,
    overflow: 'hidden' as const,
  };
};

/**
 * CSS class names for shimmer animation overlay
 */
export const shimmerOverlayClass = "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:animate-shimmer before:bg-[length:200%_100%]";
